// LearnCore Frontend - 创建学习集页面

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { InlineLoading } from '../components/Loading';
import ImageInput from '../components/ImageInput';
import TagEditor, { clearTagCache } from '../components/TagEditor';
import type { CreateStudySetRequest } from '../types';
import { 
  PlusIcon, 
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  CheckIcon,
  XMarkIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

interface FlashcardInput {
  term: string;
  definition: string;
  term_image_url?: string;
  definition_image_url?: string;
  // UI: 是否展开图片区域（无图时默认折叠）
  showImages?: boolean;
  // 卡片标签
  tag_ids?: string[];
}

export default function CreateSetPage() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_public: true
  });
  
  const [flashcards, setFlashcards] = useState<FlashcardInput[]>([
    { term: '', definition: '', term_image_url: undefined, definition_image_url: undefined, showImages: false, tag_ids: [] },
    { term: '', definition: '', term_image_url: undefined, definition_image_url: undefined, showImages: false, tag_ids: [] }
  ]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missingTags, setMissingTags] = useState<string[]>([]);
  const [creatingTags, setCreatingTags] = useState<Set<string>>(new Set());
  
  // 标签状态
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  
  // 批量导入相关状态
  const [showBatchImport, setShowBatchImport] = useState(false);
  const [batchText, setBatchText] = useState('');
  const [batchImportError, setBatchImportError] = useState<string | null>(null);
  const [pendingImportCards, setPendingImportCards] = useState<FlashcardInput[] | null>(null);
  const [pendingImportCount, setPendingImportCount] = useState<number | null>(null);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleFlashcardChange = (index: number, field: 'term' | 'definition' | 'term_image_url' | 'definition_image_url' | 'tag_ids', value: string | undefined | string[]) => {
    setFlashcards(prev => prev.map((card, i) => 
      i === index ? { ...card, [field]: value } : card
    ));
  };

  const addFlashcard = () => {
    setFlashcards(prev => [...prev, { term: '', definition: '', term_image_url: undefined, definition_image_url: undefined, showImages: false, tag_ids: [] }]);
  };

  const removeFlashcard = (index: number) => {
    if (flashcards.length <= 2) {
      alert('至少需要保留2张卡片');
      return;
    }
    setFlashcards(prev => prev.filter((_, i) => i !== index));
  };

  // 解析 Markdown 表格
  const parseMarkdownTable = (text: string): FlashcardInput[] => {
    const lines = text.trim().split('\n').map(line => line.trim()).filter(line => line);
    
    if (lines.length < 3) {
      throw new Error('Markdown 表格至少需要 3 行（标题行、分隔符行、数据行）');
    }

    // 提取 Markdown 图片：![alt](url)
    const extractImageFromMarkdown = (input: string): { text: string; imageUrl?: string } => {
      const imageRegex = /!\[(.*?)\]\((.*?)\)/;
      const match = input.match(imageRegex);
      if (!match) {
        return { text: input.trim() };
      }
      const alt = match[1]?.trim() || '';
      const url = match[2]?.trim();
      const textWithoutImage = input.replace(match[0], '').trim();
      // 若移除图片后文本为空，则回退到 alt 作为文本
      const text = textWithoutImage || alt;
      return { text, imageUrl: url };
    };

    // 解析表头
    const headerLine = lines[0];
    if (!headerLine.includes('|')) {
      throw new Error('第一行必须是表格标题行');
    }
    
    const headers = headerLine.split('|').map(cell => cell.trim()).filter(cell => cell);
    
    if (headers.length < 2) {
      throw new Error('表格至少需要两列');
    }

    // 检查分隔符行
    const separatorLine = lines[1];
    if (!separatorLine.includes('|') || !separatorLine.includes('-')) {
      throw new Error('第二行必须是表格分隔符（例如：| --- | --- |）');
    }

    // 智能识别术语和定义列
    let termColumnIndex = -1;
    let definitionColumnIndex = -1;
    // 可选：术语图片与定义图片列
    let termImageColumnIndex = -1;
    let definitionImageColumnIndex = -1;
    // 可选：标签列
    let tagColumnIndex = -1;

    // 可能的术语列名称
    const termKeywords = ['术语', '单词', '词汇', 'term', 'word', 'vocabulary', '题目', '问题', 'question'];
    // 可能的定义列名称  
    const definitionKeywords = ['定义', '解释', '答案', 'definition', 'explanation', 'answer', '含义', 'meaning', '翻译', 'translation'];

    // 查找术语列
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i].toLowerCase();
      if (termKeywords.some(keyword => header.includes(keyword.toLowerCase()))) {
        termColumnIndex = i;
        break;
      }
    }

    // 查找定义列
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i].toLowerCase();
      if (definitionKeywords.some(keyword => header.includes(keyword.toLowerCase()))) {
        definitionColumnIndex = i;
        break;
      }
    }

    // 查找术语图片与定义图片列（可选）
    const termImageKeywords = ['术语图片', '术语图', 'term image', 'term_image', 'term picture', 'term pic', 'term_img'];
    const definitionImageKeywords = ['定义图片', '定义图', 'definition image', 'definition_image', 'definition picture', 'definition pic', 'definition_img', 'answer image', 'answer_image'];
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i].toLowerCase();
      if (termImageColumnIndex === -1 && termImageKeywords.some(keyword => header.includes(keyword.toLowerCase()))) {
        termImageColumnIndex = i;
      }
      if (definitionImageColumnIndex === -1 && definitionImageKeywords.some(keyword => header.includes(keyword.toLowerCase()))) {
        definitionImageColumnIndex = i;
      }
    }

    // 查找标签列（可选）
    const tagKeywords = ['标签', 'tag', 'tags', '分类', 'category', 'categories'];
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i].toLowerCase();
      if (tagKeywords.some(keyword => header.includes(keyword.toLowerCase()))) {
        tagColumnIndex = i;
        break;
      }
    }

    // 如果没有找到匹配的列名，使用默认策略
    if (termColumnIndex === -1 && definitionColumnIndex === -1) {
      // 默认第一列为术语，第二列为定义
      termColumnIndex = 0;
      definitionColumnIndex = 1;
    } else if (termColumnIndex === -1) {
      // 找到了定义列但没找到术语列，术语列为另一列
      termColumnIndex = definitionColumnIndex === 0 ? 1 : 0;
    } else if (definitionColumnIndex === -1) {
      // 找到了术语列但没找到定义列，定义列为另一列
      definitionColumnIndex = termColumnIndex === 0 ? 1 : 0;
    }

    const result: FlashcardInput[] = [];
    
    // 从第三行开始解析数据
    for (let i = 2; i < lines.length; i++) {
      const line = lines[i];
      if (!line.includes('|')) continue;
      
      // 解析表格行
      const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell);
      
      if (cells.length >= Math.max(termColumnIndex + 1, definitionColumnIndex + 1)) {
        const termCell = cells[termColumnIndex] ?? '';
        const defCell = cells[definitionColumnIndex] ?? '';

        const { text: termTextAfter, imageUrl: termImageInline } = extractImageFromMarkdown(termCell);
        const { text: defTextAfter, imageUrl: defImageInline } = extractImageFromMarkdown(defCell);

        let term_image_url: string | undefined = termImageInline;
        let definition_image_url: string | undefined = defImageInline;

        // 若存在专门的图片列，则优先使用专门列
        if (termImageColumnIndex !== -1 && cells.length > termImageColumnIndex) {
          const termImageCell = cells[termImageColumnIndex] ?? '';
          const { imageUrl } = extractImageFromMarkdown(termImageCell);
          if (imageUrl) term_image_url = imageUrl;
        }
        if (definitionImageColumnIndex !== -1 && cells.length > definitionImageColumnIndex) {
          const defImageCell = cells[definitionImageColumnIndex] ?? '';
          const { imageUrl } = extractImageFromMarkdown(defImageCell);
          if (imageUrl) definition_image_url = imageUrl;
        }

        const term = termTextAfter.trim();
        const definition = defTextAfter.trim();

        // 解析标签（如果有标签列）
        let tag_ids: string[] = [];
        if (tagColumnIndex !== -1 && cells.length > tagColumnIndex) {
          const tagCell = cells[tagColumnIndex] ?? '';
          if (tagCell.trim()) {
            // 支持逗号和中文逗号分隔
            tag_ids = tagCell.split(/[,，]/).map(tag => tag.trim()).filter(tag => tag);
          }
        }

        if (term && definition) {
          result.push({ 
            term, 
            definition, 
            term_image_url: term_image_url, 
            definition_image_url: definition_image_url,
            tag_ids: tag_ids
          });
        }
      }
    }

    if (result.length === 0) {
      throw new Error('未找到有效的学习卡片数据');
    }

    return result;
  };

  // 处理批量导入
  const handleBatchImport = () => {
    try {
      setBatchImportError(null);
      
      if (!batchText.trim()) {
        setBatchImportError('请输入 Markdown 表格内容');
        return;
      }

      const newCards = parseMarkdownTable(batchText);
      // 进入二次确认阶段，等待用户选择“替换”或“追加”
      setPendingImportCards(newCards);
      setPendingImportCount(newCards.length);
    } catch (err: any) {
      setBatchImportError(err.message || '解析失败');
    }
  };

  const confirmExecuteBatchImport = (replaceAll: boolean) => {
    if (!pendingImportCards) return;
    const normalized = pendingImportCards.map(card => ({
      ...card,
      showImages: Boolean(card.term_image_url || card.definition_image_url)
    }));
    if (replaceAll) {
      setFlashcards(normalized);
    } else {
      setFlashcards(prev => [...prev, ...normalized]);
    }
    setShowBatchImport(false);
    setBatchText('');
    setPendingImportCards(null);
    setPendingImportCount(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 验证表单
    if (!formData.title.trim()) {
      setError('标题不能为空');
      return;
    }

    const validCards = flashcards.filter(card => 
      card.term.trim() && card.definition.trim()
    );

    if (validCards.length === 0) {
      setError('至少需要一张完整的卡片');
      return;
    }

    const requestData: CreateStudySetRequest = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      is_public: formData.is_public,
      flashcards: validCards.map(card => ({
        term: card.term.trim(),
        definition: card.definition.trim(),
        term_image_url: card.term_image_url,
        definition_image_url: card.definition_image_url,
        tag_ids: card.tag_ids || []
      }))
    };

    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.createStudySet(requestData);
      
      // 如果有标签，应用标签
      if (selectedTagIds.length > 0 && response.set?.id) {
        try {
          await apiClient.applyTagsToSet(response.set.id, selectedTagIds);
        } catch (tagErr) {
          console.error('应用标签失败:', tagErr);
          // 标签失败不阻塞创建流程
        }
      }
      
      // 创建成功，跳转到仪表盘
      navigate('/dashboard');
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData?.error_type === 'missing_tags' && errorData?.missing_tags) {
        // 显示标签缺失错误，提供一键创建功能
        setMissingTags(errorData.missing_tags);
        setError(null);
      } else {
        setError(errorData?.error || '创建学习集失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 创建缺失的标签
  const createMissingTag = async (tagName: string) => {
    try {
      setCreatingTags(prev => new Set(prev).add(tagName));
      await apiClient.createCustomTag(tagName);
      
      // 清除自定义标签缓存，强制刷新
      clearTagCache('custom');
      
      // 从缺失标签列表中移除
      setMissingTags(prev => prev.filter(name => name !== tagName));
      
      // 重新尝试创建学习集
      if (missingTags.length === 1) {
        // 如果这是最后一个缺失的标签，自动重试创建
        setTimeout(() => {
          handleSubmit({ preventDefault: () => {} } as any);
        }, 500);
      }
    } catch (err: any) {
      console.error('创建标签失败:', err);
      setError('创建标签失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreatingTags(prev => {
        const newSet = new Set(prev);
        newSet.delete(tagName);
        return newSet;
      });
    }
  };

  // 创建所有缺失的标签
  const createAllMissingTags = async () => {
    try {
      setCreatingTags(new Set(missingTags));
      
      // 批量创建标签
      const promises = missingTags.map(tagName => apiClient.createCustomTag(tagName));
      await Promise.all(promises);
      
      // 清除自定义标签缓存，强制刷新
      clearTagCache('custom');
      
      // 清空缺失标签列表
      setMissingTags([]);
      
      // 自动重试创建学习集
      setTimeout(() => {
        handleSubmit({ preventDefault: () => {} } as any);
      }, 500);
    } catch (err: any) {
      console.error('批量创建标签失败:', err);
      setError('批量创建标签失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreatingTags(new Set());
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          返回仪表盘
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          创建学习集
        </h1>
        <p className="text-gray-600">
          添加术语和定义，创建您的学习集。
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* 错误提示 */}
        {error && (
          <div className="alert-danger">
            {error}
          </div>
        )}

        {/* 缺失标签提示 */}
        {missingTags.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  以下标签不存在，请先创建：
                </h3>
                <div className="mt-2">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {missingTags.map(tagName => (
                      <button
                        key={tagName}
                        onClick={() => createMissingTag(tagName)}
                        disabled={creatingTags.has(tagName) || loading}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {creatingTags.has(tagName) ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-yellow-600" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            创建中...
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            {tagName}
                          </>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={createAllMissingTags}
                      disabled={creatingTags.size > 0 || loading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {creatingTags.size > 0 ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-yellow-600" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          批量创建中...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          一键创建所有标签
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setMissingTags([])}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-colors"
                    >
                      取消
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 基本信息 */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">基本信息</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                标题 *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                required
                className="input"
                placeholder="例如：英语单词第一章"
                value={formData.title}
                onChange={handleFormChange}
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                描述（可选）
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                className="textarea"
                placeholder="简单描述这个学习集的内容..."
                value={formData.description}
                onChange={handleFormChange}
                disabled={loading}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_public"
                name="is_public"
                checked={formData.is_public}
                onChange={handleFormChange}
                disabled={loading}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_public" className="ml-2 flex items-center text-sm text-gray-700">
                {formData.is_public ? (
                  <EyeIcon className="h-4 w-4 mr-1" />
                ) : (
                  <EyeSlashIcon className="h-4 w-4 mr-1" />
                )}
                公开学习集（其他人可以查看和学习）
              </label>
            </div>

            {/* 标签编辑器 */}
            <div className="pt-2 border-t border-gray-200">
              <TagEditor
                initialTags={[]}
                onTagsChange={setSelectedTagIds}
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* 抽认卡 */}
        <div className="card">
          <div className="card-header">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                抽认卡 ({flashcards.length})
              </h2>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => setShowBatchImport(true)}
                  className="btn-outline text-sm"
                  disabled={loading}
                >
                  <DocumentTextIcon className="h-4 w-4 mr-1" />
                  批量导入
                </button>
                <button
                  type="button"
                  onClick={addFlashcard}
                  className="btn-outline text-sm"
                  disabled={loading}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  添加卡片
                </button>
              </div>
            </div>
          </div>

          {/* 批量导入模态窗口 */}
          {showBatchImport && (
            <div className="mb-6 border-2 border-dashed border-primary-300 rounded-lg p-6 bg-primary-50">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    <DocumentTextIcon className="h-5 w-5 inline mr-2" />
                    批量导入学习卡片
                  </h3>
                  <p className="text-sm text-gray-600">
                    粘贴 Markdown 表格格式的学习内容，系统将自动解析为学习卡片
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowBatchImport(false);
                    setBatchText('');
                    setBatchImportError(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* 示例格式 */}
              <div className="mb-4 p-3 bg-white rounded border">
                <p className="text-sm font-medium text-gray-700 mb-2">示例格式（支持多种表头）：</p>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">标准格式：</p>
                    <pre className="text-xs text-gray-600 font-mono">
{`| 术语 | 定义 |
| --- | --- |
| Apple | 苹果，一种水果 |
| Book | 书籍，用于阅读的物品 |`}
                    </pre>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">颠倒顺序：</p>
                    <pre className="text-xs text-gray-600 font-mono">
{`| 定义 | 术语 |
| --- | --- |
| 苹果，一种水果 | Apple |
| 书籍，用于阅读的物品 | Book |`}
                    </pre>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">英文表头：</p>
                    <pre className="text-xs text-gray-600 font-mono">
{`| Term | Definition |
| --- | --- |
| Hello | 你好 |
| World | 世界 |`}
                    </pre>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">问答格式：</p>
                    <pre className="text-xs text-gray-600 font-mono">
{`| 问题 | 答案 |
| --- | --- |
| 1+1等于几？ | 2 |
| 中国首都是哪里？ | 北京 |`}
                    </pre>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">带标签格式：</p>
                    <pre className="text-xs text-gray-600 font-mono">
{`| 正面 | 反面 | 标签 |
| :---- | :---- | :---- |
| 在数物体的时候，用来表达物体个数的1, 2, 3……叫做{{c1::自然数}}。 | 自然数 | 数学,1年级,实数 |
| 2的平方根是{{c1::√2}} | √2 | 数学,初中,无理数 |`}
                    </pre>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  支持的关键词：术语/单词/词汇/term/word/vocabulary/题目/问题/question，定义/解释/答案/definition/explanation/answer/含义/meaning/翻译/translation，标签/tag/tags/分类/category
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  填空格式：使用 <code>{`{{c1::答案}}`}</code> 在正面创建填空题，标签用逗号分隔
                </p>
              </div>

              {/* 输入区域 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  粘贴 Markdown 表格内容：
                </label>
                <textarea
                  className="w-full h-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                  placeholder="在此粘贴 Markdown 表格..."
                  value={batchText}
                  onChange={(e) => setBatchText(e.target.value)}
                />
              </div>

              {/* 错误提示 */}
              {batchImportError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">
                    <i className="fas fa-exclamation-triangle mr-1"></i>
                    {batchImportError}
                  </p>
                </div>
              )}

              {/* 选择替换或追加 */}
              {pendingImportCount !== null && (
                <div className="mb-4 p-3 bg-white border border-gray-200 rounded">
                  <p className="text-sm text-gray-700 mb-3">
                    解析到 {pendingImportCount} 张新卡片，请选择导入方式：
                  </p>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => confirmExecuteBatchImport(true)}
                      className="btn-primary text-sm"
                      title="替换现有所有卡片"
                    >
                      替换
                    </button>
                    <button
                      type="button"
                      onClick={() => confirmExecuteBatchImport(false)}
                      className="btn-outline text-sm"
                      title="追加到现有卡片后"
                    >
                      追加
                    </button>
                  </div>
                </div>
              )}

              {/* 操作按钮 */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowBatchImport(false);
                    setBatchText('');
                    setBatchImportError(null);
                    setPendingImportCards(null);
                    setPendingImportCount(null);
                  }}
                  className="btn-outline text-sm"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handleBatchImport}
                  className="btn-primary text-sm"
                  disabled={!batchText.trim()}
                >
                  <CheckIcon className="h-4 w-4 mr-1" />
                  解析表格
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {[...flashcards].slice().reverse().map((card, index) => {
              const originalIndex = flashcards.length - 1 - index;
              return (
              <div key={originalIndex} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm font-medium text-gray-500">
                    卡片 #{index + 1}
                  </span>
                  <div className="flex items-center space-x-2">
                    {!(Boolean(card.term_image_url) || Boolean(card.definition_image_url)) && !card.showImages && (
                      <button
                        type="button"
                        onClick={() => setFlashcards(prev => prev.map((c, i) => i === originalIndex ? { ...c, showImages: true } : c))}
                        className="text-primary-600 hover:text-primary-700 transition-colors text-sm"
                        disabled={loading}
                        title="添加图片"
                      >
                        <span className="inline-flex items-center">
                          <PhotoIcon className="h-4 w-4 mr-1" /> 添加图片
                        </span>
                      </button>
                    )}
                    {flashcards.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeFlashcard(originalIndex)}
                        className="text-gray-400 hover:text-danger-600 transition-colors"
                        disabled={loading}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        术语
                      </label>
                      <input
                        type="text"
                        className="input"
                        placeholder="输入术语..."
                        value={card.term}
                        onChange={(e) => handleFlashcardChange(originalIndex, 'term', e.target.value)}
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        定义
                      </label>
                      <input
                        type="text"
                        className="input"
                        placeholder="输入定义..."
                        value={card.definition}
                        onChange={(e) => handleFlashcardChange(originalIndex, 'definition', e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* 卡片标签编辑器 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      卡片标签（可选）
                    </label>
                    <TagEditor
                      initialTags={[]}
                      onTagsChange={(tagIds) => handleFlashcardChange(originalIndex, 'tag_ids', tagIds)}
                      disabled={loading}
                    />
                  </div>

                  {(() => {
                    const hasAnyImage = Boolean(card.term_image_url) || Boolean(card.definition_image_url);
                    const expanded = hasAnyImage || card.showImages === true;
                    return expanded ? (
                      <div className="grid md:grid-cols-2 gap-4">
                        <ImageInput
                          label="术语图片（可选）"
                          imageUrl={card.term_image_url}
                          onImageChange={(url) => handleFlashcardChange(originalIndex, 'term_image_url', url)}
                          disabled={loading}
                        />
                        <ImageInput
                          label="定义图片（可选）"
                          imageUrl={card.definition_image_url}
                          onImageChange={(url) => handleFlashcardChange(originalIndex, 'definition_image_url', url)}
                          disabled={loading}
                        />
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* 提交按钮 */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="btn-outline"
            disabled={loading}
          >
            取消
          </button>
          
          <button
            type="submit"
            className="btn-primary px-8"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <InlineLoading size="sm" />
                <span>创建中...</span>
              </div>
            ) : (
              '创建学习集'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
